import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { userIamUrl } from '@/config/user-iam';
import { CALL_CENTER_IAM_LIST_PARAMS } from '@/shared/constants/call-center-iam';
import { defaultValue } from '../model/role.model';
import { serializeAxiosError, resolveAsyncThunkErrorMessage } from './reducer.utils';
import { appendPageable } from '@/shared/utils/spring-api';
import { fromIamRole, readIamRolesPage, toIamRolePayload, } from '@/shared/utils/iam-role-api';
const initialState = {
    loading: false,
    errorMessage: null,
    roles: [],
    role: defaultValue,
    updating: false,
    updateSuccess: false,
    totalItems: 0,
};
const rolesApiUrl = () => userIamUrl('/user-management/api/roles');
export const getRoles = createAsyncThunk('roleManagement/fetch_roles', async ({ page, size, sort, filter }) => {
    const q = new URLSearchParams();
    appendPageable(q, page, size, sort);
    const name = filter?.trim();
    if (name) {
        q.set('name', name);
    }
    q.set('isCallCenterRelated', CALL_CENTER_IAM_LIST_PARAMS.isCallCenterRelated);
    q.set('eagerload', 'true');
    q.set('cacheBuster', String(Date.now()));
    const response = await axios.get(`${rolesApiUrl()}?${q.toString()}`);
    const { rows, total } = readIamRolesPage(response.data, response.headers);
    return {
        ...response,
        data: rows.map(fromIamRole),
        headers: { ...response.headers, 'x-total-count': String(total) },
    };
}, { serializeError: serializeAxiosError });
export const getRole = createAsyncThunk('roleManagement/fetch_role', async (id) => {
    const response = await axios.get(`${rolesApiUrl()}/${id}`);
    return { ...response, data: fromIamRole(response.data) };
}, { serializeError: serializeAxiosError });
export const createRole = createAsyncThunk('roleManagement/create_role', async (entity) => {
    const payload = toIamRolePayload(entity);
    const response = await axios.post(rolesApiUrl(), payload);
    return { ...response, data: fromIamRole(response.data) };
}, { serializeError: serializeAxiosError });
export const updateRole = createAsyncThunk('roleManagement/update_role', async (entity) => {
    const payload = toIamRolePayload(entity);
    const id = entity.id;
    const url = id != null && id !== '' ? `${rolesApiUrl()}/${id}` : rolesApiUrl();
    const response = await axios.put(url, payload);
    return { ...response, data: fromIamRole(response.data) };
}, { serializeError: serializeAxiosError });
export const deleteRole = createAsyncThunk('roleManagement/delete_role', async (id) => {
    const response = await axios.delete(`${rolesApiUrl()}/${id}`);
    return response;
}, { serializeError: serializeAxiosError });
export const RoleManagementSlice = createSlice({
    name: 'roleManagement',
    initialState: initialState,
    reducers: {
        reset() {
            return initialState;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getRole.fulfilled, (state, action) => {
            state.loading = false;
            state.role = action.payload.data;
        })
            .addCase(deleteRole.fulfilled, state => {
            state.updating = false;
            state.updateSuccess = true;
            state.role = defaultValue;
        })
            .addMatcher(isFulfilled(getRoles), (state, action) => {
            state.loading = false;
            state.roles = action.payload.data;
            state.totalItems = parseInt(action.payload.headers['x-total-count'], 10);
        })
            .addMatcher(isFulfilled(createRole, updateRole), (state, action) => {
            state.updating = false;
            state.loading = false;
            state.updateSuccess = true;
            state.role = action.payload.data;
        })
            .addMatcher(isPending(getRoles, getRole), state => {
            state.errorMessage = null;
            state.updateSuccess = false;
            state.loading = true;
        })
            .addMatcher(isPending(createRole, updateRole, deleteRole), state => {
            state.errorMessage = null;
            state.updateSuccess = false;
            state.updating = true;
        })
            .addMatcher(isRejected(getRoles, getRole, createRole, updateRole, deleteRole), (state, action) => {
            state.loading = false;
            state.updating = false;
            state.updateSuccess = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        });
    },
});
export const { reset } = RoleManagementSlice.actions;
export default RoleManagementSlice.reducer;
